import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ApiService, GraphQLResponse } from '@lsq-frontend/angular-shared/api-service';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    ListBuyerAccountPendingGroupsRequest,
    ListResponseBuyerAccountInvoiceGroupSummaryItem
} from '@lsq-frontend/remote-services/all-java/financing-mk2-services/buyer-account/invoice-group/list-buyer-account-pending-groups';
import { BuyerAccountInvoiceGroupSummaryItem } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/buyer-account-invoice-group-summary-item';
import { FeatureFlagService } from '@lsq-frontend/common-ui';
import { EnumType, jsonToGraphQLQuery } from 'json-to-graphql-query';
import { startWith } from 'rxjs/operators';
import { mapBuyerPayableSortColumn } from '../../helpers/sort-column-mapping';
import {
    CreditMemo,
    CurrencyCode,
    Money,
    PayablesInput,
    PayablesResponse,
    PayablesSortColumn,
    Query,
    QueryFields,
    SortDirection
} from '../../model/models';
import { sumMonies } from '../../helpers/money-operations';
import {
    deDuplicatePendingCreditMemos,
    calculateTotalRemainingAmount,
    calculateAmount
} from '../../helpers/credit-memo-mapping';

@Injectable({ providedIn: 'root' })
export class ListBuyerAccountPendingGroupsService extends ApiService<
    ListBuyerAccountPendingGroupsRequest,
    ListResponseBuyerAccountInvoiceGroupSummaryItem
> {
    public requestType = ListBuyerAccountPendingGroupsRequest;
    public responseType = ListResponseBuyerAccountInvoiceGroupSummaryItem;
    public endpointName = 'BuyerAccount/InvoiceGroup/ListBuyerAccountPendingGroups';
    public serviceName = 'financing';

    private _isPayLaterEnabled = false;

    constructor(
        http: HttpClient,
        @Inject(SETTINGS) settings: ISettings,
        private _featureFlagService: FeatureFlagService
    ) {
        super(http, settings);

        this._featureFlagService
            .isOn(FeatureFlags.PayLater)
            .pipe(startWith(this._featureFlagService.isOnSync(FeatureFlags.PayLater)))
            .subscribe((isOn) => {
                this._isPayLaterEnabled = isOn;
                if (this._isPayLaterEnabled) {
                    this.endpointName = 'v1/query';
                    this.serviceName = 'financingGql';
                    this.usePascalForUrl = false;
                } else {
                    this.endpointName = 'BuyerAccount/InvoiceGroup/ListBuyerAccountPendingGroups';
                    this.serviceName = 'financing';
                    this.usePascalForUrl = true;
                }
            });
    }

    public requestMapper(
        req: ListBuyerAccountPendingGroupsRequest
    ): { query: string } | ListBuyerAccountPendingGroupsRequest {
        if (this._isPayLaterEnabled) {
            const {
                skip,
                take,
                sortColumn,
                sortDirection,
                secondarySortColumn,
                secondarySortDirection
            } = req.pagination;
            const input: PayablesInput = {
                companyFilter: {
                    buyerIds: req.buyerKeys ?? []
                },
                pagination: {
                    skip: skip ?? 0,
                    take: take ?? 0,
                    sortColumn: new EnumType(
                        mapBuyerPayableSortColumn(sortColumn) || PayablesSortColumn.NAME
                    ),
                    sortDirection: new EnumType(
                        SortDirection[sortDirection] || SortDirection.ASCENDING
                    ),
                    secondarySortColumn: new EnumType(
                        mapBuyerPayableSortColumn(secondarySortColumn)
                    ),
                    secondarySortDirection: new EnumType(
                        SortDirection[secondarySortDirection] || SortDirection.ASCENDING
                    )
                },
                pendingCreditMemosOnly: true
            };
            const amount: QueryFields<Money> = { cents: true, currencyCode: true };
            return {
                query: `{${jsonToGraphQLQuery({
                    payables: {
                        __args: { input },
                        rows: {
                            amount,
                            id: true,
                            name: true,
                            canBeRevoked: true,
                            adjustedDueDate: true,
                            isGroup: true,
                            invoiceDateParent: true,
                            relationship: {
                                supplier: {
                                    company: {
                                        id: true,
                                        name: true
                                    }
                                },
                                buyer: {
                                    company: {
                                        name: true
                                    }
                                }
                            },
                            creditMemos: {
                                id: true,
                                amount: { cents: true, currencyCode: true },
                                allocatedAmount: { cents: true, currencyCode: true }
                            }
                        }
                    }
                } as Query)}}`
            };
        }
        return req;
    }

    public responseMapper(
        res: GraphQLResponse<PayablesResponse> | ListResponseBuyerAccountInvoiceGroupSummaryItem
    ): ListResponseBuyerAccountInvoiceGroupSummaryItem {
        if (this._isPayLaterEnabled) {
            const { rows: payables } = (res as GraphQLResponse<PayablesResponse>).data.payables;

            if (!payables) {
                return new ListResponseBuyerAccountInvoiceGroupSummaryItem({
                    ok: true,
                    rows: [],
                    totalCount: 0
                });
            }

            return new ListResponseBuyerAccountInvoiceGroupSummaryItem({
                ok: true,
                totalCount: payables.length,
                rows: payables.map((payable) => {
                    const { buyer, supplier } = payable.relationship;

                    let payableAmount = payable.amount;
                    let deduplicatedCreditMemos = payable.creditMemos;

                    // All payables will be pending groups but check for memos just in case
                    if (payable.creditMemos.length > 0) {
                        deduplicatedCreditMemos = deDuplicatePendingCreditMemos(
                            payable.creditMemos
                        );
                        payableAmount = calculateAmount(deduplicatedCreditMemos);
                    }

                    return <BuyerAccountInvoiceGroupSummaryItem>{
                        canRevoke: payable.canBeRevoked,
                        businessUnitName: buyer.company.name,
                        dueDate: payable.adjustedDueDate,
                        groupId: payable.name,
                        groupKey: payable.id,
                        invoiceDate: payable.invoiceDateParent,
                        invoiceNumber: payable.name,
                        sellerKey: supplier.company.id,
                        sellerName: supplier.company.name,
                        total: payableAmount
                    };
                })
            });
        }
        return res as ListResponseBuyerAccountInvoiceGroupSummaryItem;
    }

    public get endpointUrl(): string {
        return this._isPayLaterEnabled ? super.endpointUrl.replace('/api', '') : super.endpointUrl;
    }
}
